import { Capacitor } from '@capacitor/core';
import EventEmitter from 'eventemitter3';
import PQueue from 'p-queue';
import pRetry from 'p-retry';

import AppDatabase from './db/app-db.service';
import { Directory, Filesystem } from '@capacitor/filesystem';
import {
  createProjectDir,
  projectFileExists,
  projectFilePath,
  projectFileSize,
  readProjectFileBlob,
  readProjectJsonFile,
  writeProjectFile,
  consolidateMedia
} from './db/filesystem.service';
import { cdnUrl } from './image.service';
import { COG_TILE_SIZE, CogTileBlock, listTileBlocks, planUnderzoomTiles, readCogDirectory, TIFF_COMPRESSION_WEBP, UNDERZOOM_MIN_ZOOM, UnderzoomTile } from './maps/cog-directory';
import { cogUnpackAvailable, flattenCogBlocks, onCogUnpackProgress, unpackCogBlocks } from './maps/cog-unpack';
import { CoreoMediaItem } from '../types';
import {
  appDownloadMaps,
  appDownloadMapsSuccess,
  appDownloadMapsError,
  appDownloadMedia,
  appDownloadMediaSuccess,
  appDownloadAttachments,
  appDownloadAttachmentsSuccess,
} from '../store/app/app.actions';
import { imageToImageProxy } from './image.service';
import SQLDatabase from './db/sql.service';
import { getAppAttachmentsShouldDownload, getAppAttachmentsShouldDownloadForApp, getAppMapDownloadState, getAppNetworkConnected } from '../store/selectors';
import { MediaDownloadStatus } from '../store/app/app.reducer';

export enum DownloadPriority {
  MAPS = 0,        // Highest - image/geojson have no native fallback; raster tiles can use CDN until local
  MEDIA = 1,       // Medium - used in forms
  ATTACHMENTS = 2  // Lowest - has web fallback
}

export interface DownloadProgress {
  projectId: number;
  category: 'maps' | 'media' | 'attachments';
  completed: number;
  total: number;
  currentItem?: string;
  /** Raster tile runs: the layer this event is about, with its own figures */
  layerId?: number;
  layerCompleted?: number;
  layerTotal?: number;
}

export interface RasterLayerProgress {
  completed: number;
  total: number;
}

export interface RasterTileManifest {
  tiles?: [number, number, number][];
  /** 2 when the layer is backed by a Cloud Optimized GeoTIFF (`cog`) cut into WebP tiles */
  version?: number;
  format?: string;
  cog?: string;
  minZoom?: number;
  /** Coarsest zoom held in the COG itself; tiles below it are derived from it */
  cogMinZoom?: number;
  maxZoom?: number;
  bounds?: number[];
  /** Written by the app when it saves a COG pack; see COG_PACK_VERSION */
  packVersion?: number;
  [key: string]: unknown;
}

/** Tile writes in flight at once while a COG streams in */
const COG_WRITE_CONCURRENCY = 16;
/** Tiles allowed to wait behind the in-flight writes before the stream pauses */
const COG_WRITE_BACKLOG = COG_WRITE_CONCURRENCY * 4;

/** Encode a canvas as WebP, or PNG where the WebView cannot encode WebP. */
const canvasToBlob = (canvas: HTMLCanvasElement): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    canvas.toBlob((webp) => {
      if (webp) {
        resolve(webp);
        return;
      }
      canvas.toBlob((png) => (png ? resolve(png) : reject(new Error('Canvas encoding failed'))), 'image/png');
    }, 'image/webp', 0.85);
  });
};

/** Local file the COG is downloaded to before the native plugin slices it */
const COG_LOCAL_FILE = 'layer.tif';
/**
 * Share of a COG layer's tile count that the native file transfer fills in the
 * progress reported for it; unpacking the file into tiles fills the rest. One scale
 * across both phases keeps the percentage from falling back when the unpack starts.
 */
const COG_TRANSFER_PROGRESS_SHARE = 0.5;

interface CogTileWriteResult {
  downloadedCount: number;
  failedCount: number;
  firstTileError: unknown;
}

/** A v2 manifest whose tiles can be sliced locally from one COG download. */
/**
 * Version of the on-disk pack a COG layer's saved manifest describes; 1 held only the
 * COG's own levels (older builds saved the server manifest as it came), 2 adds the
 * tiles built below them. Bumping it brings existing packs up to date on the next pass.
 */
const COG_PACK_VERSION = 2;

const isCogManifest = (manifest: RasterTileManifest): manifest is RasterTileManifest & { cog: string } => {
  return manifest.version === 2 && typeof manifest.cog === 'string' && manifest.format === 'webp';
};

export type DownloadEventTypes = {
  progress: (progress: DownloadProgress) => void;
  blockingMapsComplete: (projectId: number) => void;
  rasterLayerComplete: (projectId: number, layerId: number) => void;
  mapsComplete: (projectId: number) => void;
  error: (projectId: number, category: string, error: Error) => void;
};

const attachmentDownloadUrl = (url: string, mimeType: string): string => {
  if (mimeType?.startsWith('image')) {
    return imageToImageProxy(url, { output: 'auto' });
  }
  return url;
};

/**
 * Raster-tile packs only make sense on native, where the files are served from disk.
 * For debugging the download itself in a desktop browser (files land in the
 * Capacitor web filesystem, i.e. IndexedDB, and are never rendered) it can be
 * forced on with `localStorage.setItem('coreo:rasterTilesOnWeb', '1')`.
 */
const rasterTileDownloadsEnabled = (): boolean => {
  if (Capacitor.isNativePlatform()) {
    return true;
  }
  try {
    return globalThis.localStorage?.getItem('coreo:rasterTilesOnWeb') === '1';
  } catch {
    return false;
  }
};

/**
 * Check if an error is an abort error
 */
const isAbortError = (err: any): boolean => {
  return err?.name === 'AbortError' || err?.message === 'This operation was aborted';
};

const throwIfAborted = (signal?: AbortSignal): void => {
  if (signal?.aborted) {
    throw new DOMException('Download aborted', 'AbortError');
  }
};

type PRetryOptions = NonNullable<Parameters<typeof pRetry>[1]>;

/**
 * Raised when a raster-tile layer could not be fully downloaded. Individual tile
 * failures are tolerated while the layer downloads (so a resume only fetches what is
 * missing), but the layer as a whole must be reported as failed so Redux leaves the
 * DOWNLOADING state.
 */
export class RasterTileLayerDownloadError extends Error {
  constructor(
    public readonly layerId: number,
    public readonly downloaded: number,
    public readonly total: number,
    public readonly cause?: unknown
  ) {
    super(`Incomplete raster download for layer ${layerId}: ${downloaded}/${total} tiles`);
    this.name = 'RasterTileLayerDownloadError';
  }
}

/**
 * Raised when one or more raster-tile layers of a project failed to download.
 */
export class RasterTilesDownloadError extends Error {
  constructor(
    public readonly projectId: number,
    public readonly layerErrors: Error[]
  ) {
    super(`Failed to download raster tiles for ${layerErrors.length} layer(s) of project ${projectId}: ${layerErrors.map(e => e.message).join('; ')}`);
    this.name = 'RasterTilesDownloadError';
  }
}

/**
 * Wraps p-retry so AbortError does not trigger backoff retries (p-retry only stops on pRetry.AbortError).
 */
const safeRetry = <T>(
  fn: (attemptCount: number) => PromiseLike<T> | T,
  options?: PRetryOptions
): Promise<T> => {
  return pRetry(async (attemptCount) => {
    try {
      return await fn(attemptCount);
    } catch (err) {
      if (isAbortError(err)) {
        throw new pRetry.AbortError(err instanceof Error ? err : new Error(String(err)));
      }
      throw err;
    }
  }, options);
};

/**
 * Service for downloading project content (maps, media, attachments).
 * This is a pure download utility - it does not handle sync orchestration.
 * Action creators are responsible for calling these methods and handling success/failure.
 */
class ProjectDownloadService {
  public static instance: ProjectDownloadService = new ProjectDownloadService();

  public events = new EventEmitter<DownloadEventTypes>();
  private queue: PQueue;
  private dispatch: (action: any) => void;
  private getState: () => any;
  private initialized = false;

  // Per-project abort controllers for cancelling downloads
  private abortControllers: Map<number, AbortController> = new Map();

  // In-flight raster downloads so install/sync/loadApp share one run per project
  private rasterDownloadPromises: Map<number, Promise<void>> = new Map();
  /**
   * Native file transfers in flight, by local path. A transfer cannot be aborted, so
   * a cancelled run waits for it before the project directory is removed, and a run
   * resumed meanwhile joins it rather than starting a second one to the same path.
   */
  private cogTransfers: Map<string, { projectId: number; promise: Promise<void> }> = new Map();

  // Every in-flight download per project, so cancel() can wait for them to settle
  private activeDownloads: Map<number, Set<Promise<unknown>>> = new Map();

  // Percent of the in-flight raster run per project, once it has dispatched its start
  private rasterRunProgress: Map<number, number> = new Map();

  // Per-layer tile counts of the in-flight raster run, keyed by project then layer.
  // Only layers whose download has started have an entry.
  private rasterLayerProgress: Map<number, Map<number, RasterLayerProgress>> = new Map();

  constructor() {
    this.queue = new PQueue({ concurrency: 6 });
  }

  /**
   * Register an in-flight download for a project until it settles.
   */
  private track<T>(projectId: number, promise: Promise<T>): Promise<T> {
    let active = this.activeDownloads.get(projectId);
    if (!active) {
      active = new Set();
      this.activeDownloads.set(projectId, active);
    }
    active.add(promise);
    promise.catch(() => undefined).finally(() => {
      active.delete(promise);
      if (active.size === 0 && this.activeDownloads.get(projectId) === active) {
        this.activeDownloads.delete(projectId);
      }
    });
    return promise;
  }

  /**
   * Initialize the service with Redux dispatch and getState
   * Should be called once on app startup
   */
  init(dispatch: (action: any) => void, getState: () => any) {
    if (this.initialized) {
      return;
    }

    this.dispatch = dispatch;
    this.getState = getState;
    this.initialized = true;
  }

  /**
   * Get or create an AbortController for a project
   */
  private getAbortController(projectId: number): AbortController {
    let controller = this.abortControllers.get(projectId);
    if (!controller || controller.signal.aborted) {
      controller = new AbortController();
      this.abortControllers.set(projectId, controller);
    }
    return controller;
  }

  /**
   * Download image and geojson map layers. These have no native fallback, so install
   * awaits this before marking the project ready.
   */
  downloadBlockingMaps(projectId: number): Promise<void> {
    return this.track(projectId, this.runBlockingMapsDownload(projectId));
  }

  private async runBlockingMapsDownload(projectId: number): Promise<void> {
    const controller = this.getAbortController(projectId);

    try {
      const [imageryLayers, geojsonLayers] = await Promise.all([
        AppDatabase.instance.mapLayers.query({ sourceType: 'image', projectId }),
        AppDatabase.instance.mapLayers.query({ sourceType: 'geojson', projectId })
      ]);

      for (const layer of [...imageryLayers, ...geojsonLayers]) {
        if (controller.signal.aborted) {
          throw new DOMException('Download aborted', 'AbortError');
        }

        await this.downloadSingleFileLayer(projectId, layer, controller.signal);
        this.events.emit('progress', {
          projectId,
          category: 'maps',
          completed: 0,
          total: 0,
          currentItem: layer.name
        });
      }

      if (controller.signal.aborted) {
        throw new DOMException('Download aborted', 'AbortError');
      }

      this.events.emit('blockingMapsComplete', projectId);
    } catch (error) {
      if (!isAbortError(error)) {
        this.dispatch(appDownloadMapsError(projectId, error, 'blocking'));
        // Install waits on this phase; let the sync modal show the failure rather than
        // waiting forever for blockingMapsComplete.
        this.events.emit('error', projectId, 'maps', error instanceof Error ? error : new Error(String(error)));
      }
      throw error;
    }
  }

  /**
   * Download raster-tile pyramids in the background. Native maps use the CDN until
   * a local manifest exists. Web always reads tiles from the CDN, so this is a no-op
   * there. Safe to call from install, sync, and loadApp; concurrent calls for the
   * same project share one in-flight promise.
   */
  async downloadRasterTiles(projectId: number, options?: { skipIfIdle?: boolean }): Promise<void> {
    if (!rasterTileDownloadsEnabled()) {
      return;
    }

    const existing = this.rasterDownloadPromises.get(projectId);
    if (existing) {
      this.rejoinRasterRun(projectId);
      return existing;
    }

    const promise = this.runRasterTileDownload(projectId, options?.skipIfIdle === true).finally(() => {
      this.rasterDownloadPromises.delete(projectId);
      this.rasterRunProgress.delete(projectId);
      this.rasterLayerProgress.delete(projectId);
    });
    this.rasterDownloadPromises.set(projectId, promise);
    return this.track(projectId, promise);
  }

  /**
   * Tile counts for each layer the in-flight raster run has started, so a listener
   * attaching mid-run (e.g. the map modal opening) can show where things stand
   * without waiting for the next progress event. Empty when no run is in flight.
   */
  getRasterLayerProgress(projectId: number): ReadonlyMap<number, RasterLayerProgress> {
    return this.rasterLayerProgress.get(projectId) ?? new Map();
  }

  /**
   * A caller joining an in-flight raster run expects Redux to show it as downloading.
   * A force sync resets mapDownloadState to IDLE while the run continues, so re-dispatch
   * the start (with the current progress) when the run has started and the state no
   * longer says DOWNLOADING. Runs still scanning have not dispatched anything yet and
   * may legitimately end without a start, so leave those alone.
   */
  private rejoinRasterRun(projectId: number): void {
    const progress = this.rasterRunProgress.get(projectId);
    if (progress === undefined) {
      return;
    }
    const downloadState = getAppMapDownloadState(projectId)(this.getState());
    if (downloadState?.state === MediaDownloadStatus.DOWNLOADING) {
      return;
    }
    this.dispatch(appDownloadMaps(projectId, progress, 'raster'));
  }

  /**
   * Download all map layers (blocking image/geojson, then raster tiles).
   */
  async downloadMaps(projectId: number): Promise<void> {
    await this.downloadBlockingMaps(projectId);
    const controller = this.abortControllers.get(projectId);
    if (!controller || controller.signal.aborted) {
      throw new DOMException('Download aborted', 'AbortError');
    }
    await this.downloadRasterTiles(projectId);
  }

  private async runRasterTileDownload(projectId: number, skipIfIdle: boolean): Promise<void> {
    const controller = this.getAbortController(projectId);

    try {
      const rasterTileLayers = await AppDatabase.instance.mapLayers.query({
        sourceType: 'raster-tiles',
        projectId
      });

      // Download in the order the map settings modal lists the layers (top of the
      // stack first), so the one shown as downloading is the first in the list. The
      // modal sorts the layers it is given by `sort` (a stable sort) and lists them
      // reversed, so layers with the same sort appear in reverse query order; mirror
      // that exactly rather than breaking ties some other way.
      const stackOrder = (layer: { sort?: number | null }) => layer.sort ?? 1000;
      const orderedLayers = [...rasterTileLayers].reverse().sort((a, b) => stackOrder(b) - stackOrder(a));

      const pendingLayers: { id: number; source: string; name?: string }[] = [];
      for (const layer of orderedLayers) {
        if (await this.rasterLayerNeedsDownload(projectId, layer.id)) {
          pendingLayers.push(layer);
        }
      }

      // The scan above awaits the filesystem; a cancel() in the meantime must not be
      // followed by start/success dispatches for a project that may be going away.
      throwIfAborted(controller.signal);

      if (pendingLayers.length === 0) {
        if (skipIfIdle) {
          return;
        }
        this.dispatch(appDownloadMaps(projectId, 0, 'raster'));
        this.dispatch(appDownloadMapsSuccess(projectId, 'raster'));
        this.events.emit('mapsComplete', projectId);
        return;
      }

      if (getAppNetworkConnected(this.getState()) === false) {
        return;
      }

      throwIfAborted(controller.signal);
      this.dispatch(appDownloadMaps(projectId, 0, 'raster'));
      this.rasterRunProgress.set(projectId, 0);

      const layerDownloaded = new Map<number, number>();
      const layerTotals = new Map<number, number>();
      const startedLayers = new Map<number, RasterLayerProgress>();
      this.rasterLayerProgress.set(projectId, startedLayers);
      let lastEmittedPercent = -1;
      const lastEmittedLayerPercent = new Map<number, number>();

      // Emits when the aggregate percent or the named layer's own percent moves, so
      // listeners see each layer's first and last tile without a flood in between.
      const emitTileProgress = (layerId?: number, force = false) => {
        let completedTiles = 0;
        let totalTiles = 0;
        for (const [id, total] of layerTotals) {
          totalTiles += total;
          completedTiles += layerDownloaded.get(id) ?? 0;
        }
        const percent = totalTiles > 0 ? Math.floor((completedTiles / totalTiles) * 100) : 0;

        const layer = layerId === undefined ? undefined : startedLayers.get(layerId);
        const layerPercent = layer && layer.total > 0 ? Math.floor((layer.completed / layer.total) * 100) : 0;
        const layerMoved = layerId !== undefined && lastEmittedLayerPercent.get(layerId) !== layerPercent;
        if (!force && percent === lastEmittedPercent && !layerMoved) {
          return;
        }
        lastEmittedPercent = percent;
        if (layerId !== undefined) {
          lastEmittedLayerPercent.set(layerId, layerPercent);
        }
        this.rasterRunProgress.set(projectId, percent);
        this.events.emit('progress', {
          projectId,
          category: 'maps',
          completed: completedTiles,
          total: totalTiles,
          ...(layer ? { layerId, layerCompleted: layer.completed, layerTotal: layer.total } : {})
        });
      };

      // A failing layer must not stop the remaining layers from downloading, so collect
      // the failures and report them together once every layer has been attempted.
      const layerErrors: Error[] = [];
      const recordLayerError = (layer: { id: number }, error: unknown) => {
        console.warn(`Raster tile layer ${layer.id} failed to download:`, error);
        layerErrors.push(error instanceof Error ? error : new Error(String(error)));
      };

      // Fetch every manifest before downloading any tiles so the aggregate total is
      // known up front; otherwise the percentage would drop each time a new layer
      // started and added its tiles to the denominator.
      const manifests = new Map<number, RasterTileManifest>();
      for (const layer of pendingLayers) {
        if (controller.signal.aborted) {
          throw new DOMException('Download aborted', 'AbortError');
        }

        try {
          const manifest = await this.fetchRasterTileManifest(layer, controller.signal);
          manifests.set(layer.id, manifest);
          layerTotals.set(layer.id, manifest.tiles?.length ?? 0);
          layerDownloaded.set(layer.id, 0);
        } catch (error) {
          if (isAbortError(error)) {
            throw error;
          }
          recordLayerError(layer, error);
        }
      }
      emitTileProgress(undefined, true);

      for (const layer of pendingLayers) {
        const manifest = manifests.get(layer.id);
        if (!manifest) {
          continue;
        }
        if (controller.signal.aborted) {
          throw new DOMException('Download aborted', 'AbortError');
        }

        // Announce the layer as started at 0 so the UI can tell it apart from queued layers
        const started: RasterLayerProgress = { completed: 0, total: layerTotals.get(layer.id) ?? 0 };
        startedLayers.set(layer.id, started);
        emitTileProgress(layer.id, true);

        try {
          await this.downloadRasterTileLayer(projectId, layer, manifest, controller.signal, (downloaded, total) => {
            layerDownloaded.set(layer.id, downloaded);
            // The layer knows its real total once it has read its source (a cached
            // manifest may list fewer tiles than get written), so the aggregate follows it
            layerTotals.set(layer.id, total);
            started.completed = downloaded;
            started.total = total;
            emitTileProgress(layer.id);
          });
          this.events.emit('rasterLayerComplete', projectId, layer.id);
        } catch (error) {
          if (isAbortError(error)) {
            throw error;
          }
          recordLayerError(layer, error);
        }
        emitTileProgress(layer.id, true);
      }

      if (layerErrors.length > 0) {
        throw new RasterTilesDownloadError(projectId, layerErrors);
      }

      throwIfAborted(controller.signal);
      this.dispatch(appDownloadMapsSuccess(projectId, 'raster'));
      this.events.emit('mapsComplete', projectId);
    } catch (error) {
      if (!isAbortError(error)) {
        this.dispatch(appDownloadMapsError(projectId, error, 'raster'));
      }
      throw error;
    }
  }

  /**
   * A layer is pending until its manifest is saved, which happens after its last tile.
   * COG packs saved by a build with an older pack layout (before the tiles below the
   * COG's levels were built) are run again so those tiles get added; the tiles already
   * on disk are verified by size rather than fetched. The server's zoom range is no
   * proof of what is on disk, so only the pack version written here counts.
   */
  private async rasterLayerNeedsDownload(projectId: number, layerId: number): Promise<boolean> {
    const manifest = await readProjectJsonFile<RasterTileManifest>(projectId, 'maps', String(layerId), 'manifest.json');
    if (!manifest) {
      return true;
    }
    return isCogManifest(manifest) && (manifest.packVersion ?? 1) < COG_PACK_VERSION;
  }

  /**
   * Download a single-file map layer (image or geojson)
   */
  private async downloadSingleFileLayer(
    projectId: number,
    layer: { source: string; name?: string },
    signal?: AbortSignal
  ): Promise<void> {
    const url = new URL(layer.source);
    const filename = url.pathname.split('/').pop();
    const exists = await projectFileExists(projectId, 'maps', filename);

    if (!exists) {
      await safeRetry(async () => {
        const response = await fetch(cdnUrl(layer.source), { signal });
        if (!response.ok) {
          throw new Error(`Failed to download ${filename}: ${response.status}`);
        }
        const data = await response.blob();
        throwIfAborted(signal);
        await writeProjectFile(projectId, data, 'maps', filename);
      }, {
        retries: 3,
        minTimeout: 1000,
        maxTimeout: 10000,
        onFailedAttempt: (error) => {
          console.warn(`Download ${filename} failed, attempt ${error.attemptNumber}:`, error.message);
        }
      });
      console.log(`Downloaded ${filename}`);
    }
  }

  /**
   * Fetch the tile manifest for a raster-tiles layer from the CDN (with retry).
   */
  private async fetchRasterTileManifest(
    layer: { id: number; source: string },
    signal?: AbortSignal
  ): Promise<RasterTileManifest> {
    return safeRetry(async () => {
      const manifestUrl = `${layer.source}/manifest.json`;
      const response = await fetch(cdnUrl(manifestUrl), { signal });
      if (!response.ok) {
        throw new Error(`Failed to fetch manifest: ${response.status}`);
      }
      return response.json();
    }, {
      retries: 3,
      minTimeout: 1000,
      onFailedAttempt: (error) => {
        console.warn(`Manifest fetch for layer ${layer.id} failed, attempt ${error.attemptNumber}:`, error.message);
      }
    });
  }

  /**
   * Download all tiles listed in a raster-tiles layer manifest.
   *
   * Resolves once the full pyramid and its manifest are on disk. Rejects with
   * RasterTileLayerDownloadError when any tile could not be fetched (the tiles that did
   * download are kept so a later run only fetches the missing ones).
   */
  private async downloadRasterTileLayer(
    projectId: number,
    layer: { id: number; source: string; name?: string },
    manifest: RasterTileManifest,
    signal?: AbortSignal,
    onProgress?: (downloaded: number, total: number) => void
  ): Promise<void> {
    if (isCogManifest(manifest)) {
      return this.downloadCogTileLayer(projectId, layer, manifest, signal, onProgress);
    }

    const layerId = layer.id;
    const rootUrl = layer.source;

    // Create directory structure for this layer
    await createProjectDir(projectId, `maps/${layerId}`);

    // Download tiles using a queue for concurrency control
    const tileQueue = new PQueue({ concurrency: 6 });
    let downloadedCount = 0;
    let failedCount = 0;
    let firstTileError: unknown;
    const totalTiles = manifest.tiles?.length || 0;

    console.log(`Downloading ${totalTiles} tiles for layer ${layerId}`);
    onProgress?.(0, totalTiles);

    // Queue all tile downloads with retry
    for (const tile of manifest.tiles || []) {
      tileQueue.add(async () => {
        // Check if aborted
        if (signal?.aborted) {
          return;
        }

        const [z, x, y] = tile;
        try {
          const alreadyHaveTile = await projectFileExists(
            projectId,
            'maps',
            String(layerId),
            String(z),
            String(x),
            `${y}.png`
          );
          if (!alreadyHaveTile) {
            await safeRetry(async () => {
              const tileUrl = `${rootUrl}/${z}/${x}/${y}.png`;
              const response = await fetch(cdnUrl(tileUrl), { signal });
              if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
              }
              const data = await response.blob();
              throwIfAborted(signal);

              // Create zoom directory if needed
              await createProjectDir(projectId, `maps/${layerId}/${z}/${x}`);

              // Write tile file
              await writeProjectFile(projectId, data, 'maps', String(layerId), String(z), String(x), `${y}.png`);
            }, {
              retries: 2,
              minTimeout: 500
            });
          }

          downloadedCount++;
          if (downloadedCount % 50 === 0 || downloadedCount === totalTiles) {
            console.log(`Downloaded ${downloadedCount}/${totalTiles} tiles for layer ${layerId}`);
            onProgress?.(downloadedCount, totalTiles);
          }
        } catch (err) {
          if (isAbortError(err)) {
            return;
          }
          // Keep going so the other tiles land on disk, but remember the failure — the
          // manifest is only written (and the layer only reported complete) when every
          // tile succeeded.
          console.warn(`Error downloading tile ${z}/${x}/${y}:`, err);
          failedCount++;
          if (firstTileError === undefined) {
            firstTileError = err;
          }
        }
      }, { priority: DownloadPriority.MAPS });
    }

    // If aborted, clear the tile queue
    if (signal?.aborted) {
      tileQueue.clear();
      throw new DOMException('Download aborted', 'AbortError');
    }

    // Wait for all tile downloads to complete
    await tileQueue.onIdle();

    // Check if aborted before saving manifest
    if (signal?.aborted) {
      throw new DOMException('Download aborted', 'AbortError');
    }

    if (downloadedCount !== totalTiles) {
      console.warn(`Incomplete raster download for layer ${layerId}: ${downloadedCount}/${totalTiles} tiles (${failedCount} failed)`);
      onProgress?.(downloadedCount, totalTiles);
      throw new RasterTileLayerDownloadError(layerId, downloadedCount, totalTiles, firstTileError);
    }

    // Save manifest locally only once the full pyramid is on disk
    const manifestBlob = new Blob([JSON.stringify(manifest)], { type: 'application/json' });
    await writeProjectFile(projectId, manifestBlob, 'maps', String(layerId), 'manifest.json');
    // A cancel() that landed during the write must not be reported as a completed layer
    throwIfAborted(signal);
    onProgress?.(downloadedCount, totalTiles);

    console.log(`Completed downloading ${downloadedCount}/${totalTiles} tiles for layer ${layerId}`);
  }

  /**
   * Download a raster-tiles layer backed by a Cloud Optimized GeoTIFF.
   *
   * The COG's 256-px WebP blocks are the XYZ tiles (see cog-directory.ts), so instead
   * of one request per tile the directory is read with a few range requests and the
   * block data is streamed once, each block being written to
   * `maps/{layerId}/{z}/{x}/{y}.webp` as it arrives. Blocks already on disk from an
   * earlier attempt are skipped, so a resume only streams from the first missing block.
   * Memory stays bounded by the largest block plus one network chunk.
   */
  private async downloadCogTileLayer(
    projectId: number,
    layer: { id: number; source: string; name?: string },
    manifest: RasterTileManifest & { cog: string },
    signal?: AbortSignal,
    onProgress?: (downloaded: number, total: number) => void
  ): Promise<void> {
    const layerId = layer.id;
    const cogUrl = cdnUrl(manifest.cog);
    const retryOptions: PRetryOptions = {
      retries: 2,
      minTimeout: 1000,
      onFailedAttempt: (error) => {
        console.warn(`COG download for layer ${layerId} failed, attempt ${error.attemptNumber}:`, error.message);
      }
    };

    const rangeRead = async (offset: number, length: number): Promise<Uint8Array> => {
      const response = await fetch(cogUrl, { signal, headers: { Range: `bytes=${offset}-${offset + length - 1}` } });
      if (response.status === 206) {
        return new Uint8Array(await response.arrayBuffer());
      }
      if (response.status === 200) {
        // Origin ignored the range; take the slice we asked for
        return new Uint8Array(await response.arrayBuffer()).subarray(offset, offset + length);
      }
      throw new Error(`HTTP ${response.status}`);
    };

    const directory = await safeRetry(() => readCogDirectory(rangeRead), retryOptions);
    if (directory.compression !== TIFF_COMPRESSION_WEBP) {
      throw new Error(`COG for layer ${layerId} is not WebP compressed (${directory.compression})`);
    }
    const blocks = listTileBlocks(directory);
    // Tiles below the COG's levels are built from it afterwards; the manifest lists
    // them too, so they count towards the layer's total like downloaded tiles
    const underzoom = planUnderzoomTiles(directory);
    const totalTiles = blocks.length + underzoom.length;
    const nativeUnpack = cogUnpackAvailable();
    console.log(`Downloading ${blocks.length} tiles for layer ${layerId} from ${cogUrl} (${nativeUnpack ? 'native unpack' : 'streamed'}), deriving ${underzoom.length}`);
    onProgress?.(0, totalTiles);

    const { downloadedCount, failedCount, firstTileError } = nativeUnpack
      ? await this.unpackCogNatively(projectId, layerId, cogUrl, blocks, totalTiles, retryOptions, signal, onProgress)
      : await this.streamCogTiles(projectId, layerId, cogUrl, blocks, totalTiles, retryOptions, signal, onProgress);

    if (signal?.aborted) {
      throw new DOMException('Download aborted', 'AbortError');
    }

    if (downloadedCount !== blocks.length) {
      console.warn(`Incomplete raster download for layer ${layerId}: ${downloadedCount}/${blocks.length} tiles (${failedCount} failed)`);
      onProgress?.(downloadedCount, totalTiles);
      throw new RasterTileLayerDownloadError(layerId, downloadedCount, totalTiles, firstTileError);
    }

    // Zooms coarser than the COG holds, so the layer is visible zoomed out offline too
    let completed = downloadedCount;
    await this.buildUnderzoomTiles(projectId, layerId, underzoom, signal, () => {
      completed++;
      onProgress?.(completed, totalTiles);
    });

    // Save manifest locally only once every tile is on disk; its `format` tells
    // getRasterTileUrl to serve `.webp` files. The pack version and zoom range record
    // what is on disk, not what a possibly cached manifest said.
    const saved = { ...manifest, packVersion: COG_PACK_VERSION, minZoom: UNDERZOOM_MIN_ZOOM, cogMinZoom: directory.minZoom };
    const manifestBlob = new Blob([JSON.stringify(saved)], { type: 'application/json' });
    await writeProjectFile(projectId, manifestBlob, 'maps', String(layerId), 'manifest.json');
    throwIfAborted(signal);
    onProgress?.(completed, totalTiles);

    console.log(`Completed downloading ${completed}/${totalTiles} tiles for layer ${layerId}`);
  }

  /**
   * Web / fallback path: stream the block data once and write each tile from JS as
   * it arrives. Blocks already on disk from an earlier attempt are skipped, so a
   * resume only streams from the first missing block. Memory stays bounded by the
   * largest block plus one network chunk.
   */
  private async streamCogTiles(
    projectId: number,
    layerId: number,
    cogUrl: string,
    blocks: CogTileBlock[],
    totalTiles: number,
    retryOptions: PRetryOptions,
    signal?: AbortSignal,
    onProgress?: (downloaded: number, total: number) => void
  ): Promise<CogTileWriteResult> {
    // Only stat each tile when an earlier attempt may have left some behind
    let mayHaveTiles = await projectFileExists(projectId, 'maps', String(layerId));
    await createProjectDir(projectId, `maps/${layerId}`);
    const createdDirs = new Set<string>();
    const tileFile = (block: CogTileBlock) => [String(layerId), String(block.z), String(block.x), `${block.y}.webp`] as const;

    const findPending = async (): Promise<CogTileBlock[]> => {
      if (!mayHaveTiles) {
        return blocks;
      }
      const pending: CogTileBlock[] = [];
      for (const block of blocks) {
        throwIfAborted(signal);
        // A tile is only done when the whole block is on disk: a write cut short by the
        // app being killed leaves a shorter file, which must be fetched again.
        if ((await projectFileSize(projectId, 'maps', ...tileFile(block))) !== block.length) {
          pending.push(block);
        }
      }
      return pending;
    };

    const writeBlock = async (block: CogTileBlock, bytes: Uint8Array<ArrayBuffer>): Promise<void> => {
      const dir = `maps/${layerId}/${block.z}/${block.x}`;
      if (!createdDirs.has(dir)) {
        await createProjectDir(projectId, dir);
        createdDirs.add(dir);
      }
      const blob = new Blob([bytes], { type: 'image/webp' });
      await writeProjectFile(projectId, blob, 'maps', ...tileFile(block));
    };

    let downloadedCount = 0;
    let failedCount = 0;
    let firstTileError: unknown;

    const streamBlocks = async (pending: CogTileBlock[]): Promise<void> => {
      if (pending.length === 0) {
        return;
      }
      const first = pending[0];
      const last = pending[pending.length - 1];
      const response = await fetch(cogUrl, {
        signal,
        headers: { Range: `bytes=${first.offset}-${last.offset + last.length - 1}` }
      });
      let position: number;
      if (response.status === 206) {
        position = first.offset;
      } else if (response.status === 200) {
        position = 0;
      } else {
        throw new Error(`HTTP ${response.status}`);
      }
      if (!response.body) {
        throw new Error('COG response has no body');
      }

      // Writes run a little behind the network; bound the backlog so a slow disk
      // cannot let the buffered stream grow without limit. Each write is one local
      // HTTP PUT into the native blob writer, so the per-file overhead dominates and
      // the queue is the pace-setter: keep it wide.
      const writes = new PQueue({ concurrency: COG_WRITE_CONCURRENCY });
      const chunks: Uint8Array[] = [];
      let buffered = 0;
      let next = 0;

      const take = (length: number): Uint8Array<ArrayBuffer> => {
        // Assemble `length` bytes from the front of the chunk list
        const out = new Uint8Array(length);
        let filled = 0;
        while (filled < length) {
          const head = chunks[0];
          const n = Math.min(head.byteLength, length - filled);
          out.set(head.subarray(0, n), filled);
          filled += n;
          if (n === head.byteLength) {
            chunks.shift();
          } else {
            chunks[0] = head.subarray(n);
          }
        }
        buffered -= length;
        position += length;
        return out;
      };

      const discard = (length: number): void => {
        take(length);
      };

      const reader = response.body.getReader();
      try {
        while (next < pending.length) {
          const block = pending[next];
          if (block.offset > position) {
            // Gap between blocks (sparse or already-downloaded data)
            const gap = block.offset - position;
            if (buffered < gap) {
              const { done, value } = await reader.read();
              if (done) {
                throw new Error('COG stream ended early');
              }
              chunks.push(value);
              buffered += value.byteLength;
              continue;
            }
            discard(gap);
          }
          if (buffered < block.length) {
            const { done, value } = await reader.read();
            if (done) {
              throw new Error('COG stream ended early');
            }
            chunks.push(value);
            buffered += value.byteLength;
            continue;
          }
          const bytes = take(block.length);
          next++;
          throwIfAborted(signal);
          await writes.onSizeLessThan(COG_WRITE_BACKLOG);
          writes.add(async () => {
            try {
              await writeBlock(block, bytes);
              downloadedCount++;
              if (downloadedCount % 50 === 0 || downloadedCount === blocks.length) {
                onProgress?.(downloadedCount, totalTiles);
              }
            } catch (err) {
              if (isAbortError(err)) {
                return;
              }
              console.warn(`Error writing tile ${block.z}/${block.x}/${block.y}:`, err);
              failedCount++;
              if (firstTileError === undefined) {
                firstTileError = err;
              }
            }
          });
        }
      } finally {
        reader.cancel().catch(() => undefined);
        await writes.onIdle();
      }
    };

    await safeRetry(async () => {
      throwIfAborted(signal);
      const pending = await findPending();
      downloadedCount = blocks.length - pending.length;
      failedCount = 0;
      firstTileError = undefined;
      mayHaveTiles = true;
      await streamBlocks(pending);
    }, retryOptions);

    return { downloadedCount, failedCount, firstTileError };
  }

  /**
   * Native path: download the whole COG to `maps/{layerId}/layer.tif` with the
   * Filesystem plugin (no bridge traffic per chunk), then hand the block list to the
   * CogUnpack plugin, one zoom level per call, which slices the tiles out with native
   * file IO. The download is the slow part, so progress during it is reported as the
   * matching fraction of tiles; the unpack takes seconds.
   *
   * A complete `layer.tif` left by an earlier, interrupted run is reused rather than
   * downloaded again. The native download cannot be cancelled mid-flight: on abort the
   * promise rejects straight away and the file finishes in the background.
   */
  private async unpackCogNatively(
    projectId: number,
    layerId: number,
    cogUrl: string,
    blocks: CogTileBlock[],
    totalTiles: number,
    retryOptions: PRetryOptions,
    signal?: AbortSignal,
    onProgress?: (downloaded: number, total: number) => void
  ): Promise<CogTileWriteResult> {
    const blockCount = blocks.length;
    const localPath = projectFilePath(projectId, 'maps', String(layerId), COG_LOCAL_FILE);
    const lastByte = blocks.reduce((end, block) => Math.max(end, block.offset + block.length), 0);

    // Progress runs on one scale: the transfer fills the first share of the block
    // count and the unpack the remainder, so it never drops between the two. The
    // tiles derived afterwards (`totalTiles - blockCount`) are reported by the caller.
    const transferUnits = Math.floor(blockCount * COG_TRANSFER_PROGRESS_SHARE);
    const unpacked = (written: number) => transferUnits + Math.floor((written / Math.max(1, blockCount)) * (blockCount - transferUnits));

    const existingSize = await projectFileSize(projectId, 'maps', String(layerId), COG_LOCAL_FILE);
    if (existingSize === null || existingSize < lastByte) {
      // Android's downloadFile opens the target directly and ignores `recursive`, so
      // the layer directory must exist before the transfer starts (iOS creates it)
      await createProjectDir(projectId, `maps/${layerId}`);
      await safeRetry(async () => {
        throwIfAborted(signal);
        await this.downloadCogFile(projectId, cogUrl, localPath, transferUnits, totalTiles, signal, onProgress);
        const size = await projectFileSize(projectId, 'maps', String(layerId), COG_LOCAL_FILE);
        if (size === null || size < lastByte) {
          throw new Error(`COG download for layer ${layerId} is incomplete (${size ?? 0} of at least ${lastByte} bytes)`);
        }
      }, retryOptions);
    } else {
      console.log(`Reusing downloaded COG for layer ${layerId} (${existingSize} bytes)`);
    }
    throwIfAborted(signal);
    onProgress?.(transferUnits, totalTiles);

    const cogUri = (await Filesystem.getUri({ directory: Directory.Data, path: localPath })).uri;
    const outUri = (await Filesystem.getUri({ directory: Directory.Data, path: projectFilePath(projectId, 'maps', String(layerId)) })).uri;

    // One call per zoom level keeps each bridge payload modest
    const byZoom = new Map<number, CogTileBlock[]>();
    for (const block of blocks) {
      const group = byZoom.get(block.z);
      if (group) {
        group.push(block);
      } else {
        byZoom.set(block.z, [block]);
      }
    }

    let downloadedCount = 0;
    let failedCount = 0;
    let firstTileError: unknown;
    // Progress events are plugin-wide, so another project's unpack would be counted
    // here too without the request id to tell them apart
    const requestId = `${projectId}/${layerId}`;
    const progressHandle = await onCogUnpackProgress((progress) => {
      if (progress.requestId !== requestId) {
        return;
      }
      onProgress?.(Math.min(unpacked(downloadedCount + progress.written), blockCount), totalTiles);
    });
    try {
      for (const [zoom, group] of [...byZoom.entries()].sort(([a], [b]) => a - b)) {
        throwIfAborted(signal);
        const result = await unpackCogBlocks({ requestId, cogPath: cogUri, outDir: outUri, blocks: flattenCogBlocks(group) });
        downloadedCount += result.written;
        failedCount += result.failed;
        if (result.failed > 0 && firstTileError === undefined) {
          firstTileError = new Error(result.firstError ?? `Failed to write ${result.failed} tiles at zoom ${zoom}`);
        }
        onProgress?.(unpacked(downloadedCount), totalTiles);
      }
    } finally {
      await progressHandle.remove();
    }

    if (downloadedCount === blockCount) {
      // Tiles are the source of truth now; the COG itself is no longer needed
      await Filesystem.deleteFile({ directory: Directory.Data, path: localPath }).catch(() => undefined);
    }
    return { downloadedCount, failedCount, firstTileError };
  }

  /**
   * Download a file to the project directory with the native Filesystem plugin,
   * mapping byte progress onto the first `transferUnits` of the layer's tile count.
   * Rejects immediately on abort, though the native transfer itself runs on to
   * completion: it stays tracked so cancel() can wait for it, and a run resumed
   * before then joins it instead of starting another transfer to the same path.
   */
  private async downloadCogFile(
    projectId: number,
    url: string,
    localPath: string,
    transferUnits: number,
    totalTiles: number,
    signal?: AbortSignal,
    onProgress?: (downloaded: number, total: number) => void
  ): Promise<void> {
    let lastReported = -1;
    const progressHandle = await Filesystem.addListener('progress', (status) => {
      if (status.url !== url || !status.contentLength) {
        return;
      }
      const units = Math.min(transferUnits, Math.floor((status.bytes / status.contentLength) * transferUnits));
      if (units !== lastReported) {
        lastReported = units;
        onProgress?.(Math.max(0, units), totalTiles);
      }
    });
    try {
      let transfer = this.cogTransfers.get(localPath)?.promise;
      if (!transfer) {
        transfer = Filesystem.downloadFile({
          url,
          path: localPath,
          directory: Directory.Data,
          progress: true,
          recursive: true
        }).then(() => undefined).finally(() => {
          this.cogTransfers.delete(localPath);
        });
        this.cogTransfers.set(localPath, { projectId, promise: transfer });
      }
      if (!signal) {
        await transfer;
        return;
      }
      await new Promise<void>((resolve, reject) => {
        const onAbort = () => reject(new DOMException('Download aborted', 'AbortError'));
        if (signal.aborted) {
          onAbort();
          return;
        }
        signal.addEventListener('abort', onAbort, { once: true });
        transfer.then(() => resolve(), reject).finally(() => signal.removeEventListener('abort', onAbort));
      });
    } finally {
      await progressHandle.remove();
    }
  }

  /**
   * Build the tiles below the COG's coarsest level from the tiles already on disk,
   * one level at a time: each tile is its four children at z+1 drawn into 128-px
   * quadrants. The coarsest COG level is only a handful of tiles, so this is a few
   * dozen small canvases. Mapbox sniffs the image type, so a PNG from a WebView
   * that cannot encode WebP still renders from the `.webp` path.
   */
  private async buildUnderzoomTiles(
    projectId: number,
    layerId: number,
    plan: UnderzoomTile[],
    signal?: AbortSignal,
    onTile?: () => void
  ): Promise<void> {
    if (plan.length === 0) {
      return;
    }
    const canvas = document.createElement('canvas');
    canvas.width = COG_TILE_SIZE;
    canvas.height = COG_TILE_SIZE;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error(`No 2D canvas to build tiles below zoom ${plan[0].z + 1} for layer ${layerId}`);
    }
    const half = COG_TILE_SIZE / 2;
    const createdDirs = new Set<string>();
    for (const tile of plan) {
      throwIfAborted(signal);
      ctx.clearRect(0, 0, COG_TILE_SIZE, COG_TILE_SIZE);
      for (const child of tile.children) {
        const childPath = `${tile.z + 1}/${child.x}/${child.y}.webp`;
        const blob = await readProjectFileBlob(projectId, 'maps', String(layerId), childPath);
        if (!blob) {
          // The plan only names tiles that exist (sparse blocks are left out), so a
          // missing one means the pack is damaged; a parent built without it would
          // be saved as complete with a hole in it
          throw new Error(`Tile ${childPath} of layer ${layerId} is missing; cannot build ${tile.z}/${tile.x}/${tile.y}`);
        }
        const bitmap = await createImageBitmap(blob);
        try {
          ctx.drawImage(bitmap, (child.x - tile.x * 2) * half, (child.y - tile.y * 2) * half, half, half);
        } finally {
          bitmap.close();
        }
      }
      const out = await canvasToBlob(canvas);
      const dir = `maps/${layerId}/${tile.z}/${tile.x}`;
      if (!createdDirs.has(dir)) {
        await createProjectDir(projectId, dir);
        createdDirs.add(dir);
      }
      await writeProjectFile(projectId, out, 'maps', String(layerId), String(tile.z), String(tile.x), `${tile.y}.webp`);
      onTile?.();
    }
    console.log(`Built ${plan.length} tiles below zoom ${plan[0].z + 1} for layer ${layerId}`);
  }

  /**
   * Download all media items for a project
   */
  downloadMedia(projectId: number): Promise<void> {
    return this.track(projectId, this.runMediaDownload(projectId));
  }

  private async runMediaDownload(projectId: number): Promise<void> {
    const controller = this.getAbortController(projectId);
    const state = this.getState();
    const currentState = state?.app?.apps?.find(a => a.id === projectId)?.mediaDownloadState;

    // Check if already downloading
    if (currentState?.state === MediaDownloadStatus.DOWNLOADING) {
      return;
    }

    const currentMedia = await AppDatabase.instance.mediaItems.query({ projectId });
    const toDownload = await consolidateMedia(projectId, 'media', currentMedia);

    if (toDownload.length === 0) {
      this.dispatch(appDownloadMediaSuccess(projectId));
      return;
    }

    this.dispatch(appDownloadMedia(projectId, 0));

    let completed = 0;
    const total = toDownload.length;
    const promises: Promise<void>[] = [];

    for (const item of toDownload) {
      const promise = this.queue.add(async ({ signal }) => {
        try {
          await this.downloadMediaItem(projectId, item, signal);
          completed++;
          this.events.emit('progress', {
            projectId,
            category: 'media',
            completed,
            total,
            currentItem: item.name
          });
        } catch (err) {
          if (!isAbortError(err)) {
            console.warn(`Failed to download media item ${item.id}:`, err);
          }
        }
      }, {
        signal: controller.signal,
        priority: DownloadPriority.MEDIA
      }).catch(err => {
        // Ignore abort errors - task was removed from queue
        if (!isAbortError(err)) {
          throw err;
        }
      });

      promises.push(promise);
    }

    // Wait for all media downloads to complete (or be aborted)
    await Promise.allSettled(promises);

    // Only dispatch success if not aborted
    if (!controller.signal.aborted) {
      this.dispatch(appDownloadMediaSuccess(projectId));
    }
  }

  /**
   * Download a single media item with retry
   */
  private async downloadMediaItem(
    projectId: number,
    item: CoreoMediaItem,
    signal?: AbortSignal
  ): Promise<void> {
    const downloadUrl = attachmentDownloadUrl(item.url, item.type);

    await safeRetry(async () => {
      const response = await fetch(downloadUrl, { cache: 'default', signal });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.blob();
      throwIfAborted(signal);
      await writeProjectFile(projectId, data, 'media', item.id);
    }, {
      retries: 3,
      minTimeout: 1000,
      onFailedAttempt: (error) => {
        console.warn(`Media download failed, attempt ${error.attemptNumber}:`, error.message);
      }
    });
  }

  /**
   * Download all attachments for a project
   */
  downloadAttachments(projectId: number): Promise<void> {
    return this.track(projectId, this.runAttachmentsDownload(projectId));
  }

  private async runAttachmentsDownload(projectId: number): Promise<void> {
    const controller = this.getAbortController(projectId);

    // Check if settings allow attachment downloads
    if (!getAppAttachmentsShouldDownload(this.getState())) {
      return;
    }

    const visited = new Set<number>();
    let toSync = await AppDatabase.instance.attachments.getUnsynchronised(projectId);

    if (toSync.length === 0) {
      this.dispatch(appDownloadAttachmentsSuccess(projectId));
      return;
    }

    this.dispatch(appDownloadAttachments(projectId, 0));

    while (toSync.length > 0) {
      for (const attachment of toSync) {
        // Check if aborted
        if (controller.signal.aborted) {
          return;
        }

        visited.add(attachment.id);

        try {
          const downloadUrl = attachmentDownloadUrl(attachment.url, attachment.mimeType);

          await safeRetry(async () => {
            const response = await fetch(downloadUrl, { signal: controller.signal });
            if (!response.ok) {
              throw new Error(`HTTP ${response.status}`);
            }
            const data = await response.blob();
            throwIfAborted(controller.signal);
            const path = await writeProjectFile(projectId, data, 'attachments', attachment.id);

            // Update database with file location
            const transaction = await SQLDatabase.instance.transaction();
            try {
              await transaction.executeSet([AppDatabase.instance.attachments.setFileLocationSet(attachment.id, path)]);
              await transaction.commit();
            } catch (e) {
              await transaction.rollback();
              throw e;
            }
          }, {
            retries: 3,
            minTimeout: 1000
          });

          this.events.emit('progress', {
            projectId,
            category: 'attachments',
            completed: visited.size,
            total: toSync.length + visited.size,
            currentItem: attachment.url
          });

        } catch (err) {
          if (isAbortError(err)) {
            return;
          }
          console.warn(`Failed to download attachment ${attachment.id}:`, err);
        }

        // Check if we should continue
        if (!getAppAttachmentsShouldDownloadForApp(projectId)(this.getState())) {
          return;
        }
      }

      // Check for more attachments
      toSync = (await AppDatabase.instance.attachments.getUnsynchronised(projectId))
        .filter(a => !visited.has(a.id));
    }

    // Only dispatch success if not aborted
    if (!controller.signal.aborted) {
      this.dispatch(appDownloadAttachmentsSuccess(projectId));
    }
  }

  /**
   * Abort downloads for a specific project
   * Only affects that project's downloads - other projects continue unaffected
   */
  abort(projectId: number): void {
    const controller = this.abortControllers.get(projectId);
    if (controller) {
      controller.abort();
      this.abortControllers.delete(projectId);
    }
  }

  /**
   * Abort a project's downloads and wait until every in-flight task (raster tiles,
   * blocking maps, media, attachments) has settled. Call before deleting the
   * project's files or database so nothing is written into, or reported for, a
   * project that no longer exists.
   */
  async cancel(projectId: number): Promise<void> {
    this.abort(projectId);
    const active = [...(this.activeDownloads.get(projectId) ?? [])];
    const transfers = [...this.cogTransfers.values()].filter(t => t.projectId === projectId).map(t => t.promise);
    if (active.length === 0 && transfers.length === 0) {
      return;
    }
    await Promise.allSettled([...active, ...transfers]);
  }

  /**
   * True while any download for the project is in flight.
   */
  hasActiveDownloads(projectId: number): boolean {
    return (this.activeDownloads.get(projectId)?.size ?? 0) > 0;
  }

  /**
   * Clean up when service is destroyed
   */
  destroy(): void {
    // Abort all project downloads
    for (const controller of this.abortControllers.values()) {
      controller.abort();
    }
    this.abortControllers.clear();
    this.rasterDownloadPromises.clear();
    this.rasterRunProgress.clear();
    this.rasterLayerProgress.clear();
    this.activeDownloads.clear();
    this.queue.clear();
  }
}

export default ProjectDownloadService;
